import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ErrorHandlingService} from '../common/error-handling.service';
import {Observable} from 'rxjs';
import {Account, AccountType} from './account.model';
import {
  CommonPaginationResponse,
  CommonResponse,
  PaginateResponse,
  processNormal,
  processPagination, VoidResponse
} from '../common/response.model';
import {environment} from '../../../../environments/environment';
import {catchError, map} from 'rxjs/operators';
import {AccountForm} from '../../FormModels/account-form.model';

@Injectable()
export class AccountsService {

  constructor(private http: HttpClient, private errorHandling: ErrorHandlingService) {
  }


  // ABM de cuentas

  /**
   * Obtener el listado de todas las cuentas sin paginacion
   *
   * @return Observable<Account[]>
   */
  public listAll(includePatient: boolean = false): Observable<Account[]> {

    let params = new HttpParams().append('no-paginate', '1');
    if (includePatient) { params = params.append('include_patient', 'true'); }

    return this.http.get<CommonResponse<Account[]>>(
      environment.apiUrl + 'accounts',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
   * Obtener el listado de todas las cuentas con paginacion
   *
   * @return Observable<PaginateResponse<Account[]>>
   */
  public listPaginated(page, per_page, filter = ''): Observable<PaginateResponse<Account[]>> {
    let params = new HttpParams().append('paginate_per_page', per_page.toString()).append('page', page);
    if (filter !== '') { params = params.append('filter', filter); }

    return this.http.get<CommonPaginationResponse<Account[]>>(
      environment.apiUrl + 'accounts',
      {
        params
      }
    ).pipe(
      map(resp => processPagination(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
   * Agregar un account a la plataforma
   *
   * @return Observable<Account>
   */
  public add(account: AccountForm): Observable<Account> {

    // Agregar las configuraciones del account
    if (account.add_doctor_enabled !== undefined) {
      account.configurations = JSON.stringify({enabled_for_adding_doctors: account.add_doctor_enabled});
    }

    return this.http.post<CommonResponse<Account>>(
      environment.apiUrl + 'accounts',
      account
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
   * Ver datos de un account
   *
   * @return Observable<Account>
   */
  public show(id: string): Observable<Account> {

    return this.http.get<CommonResponse<Account>>(
      environment.apiUrl + 'accounts/' + id
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
   * Editar datos del account
   *
   * @return Observable<Account>
   */
  public edit(id: string, account: AccountForm): Observable<Account> {

    // Agregar las configuraciones del account
    if (account.add_doctor_enabled !== undefined) {
      account.configurations = JSON.stringify({enabled_for_adding_doctors: account.add_doctor_enabled});
    }

    return this.http.put<CommonResponse<Account>>(
      environment.apiUrl + 'accounts/' + id,
      account
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
   * Eliminar el account
   *
   * @return Observable<VoidResponse>
   */
  public delete(id: string): Observable<VoidResponse> {

    return this.http.delete<CommonResponse<VoidResponse>>(
      environment.apiUrl + 'accounts/' + id
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
   * Obtener los tipos de cuentas
   *
   * @return Observable<AccountType[]>
   */
  public listTypes(): Observable<AccountType[]> {
    return this.http.get<CommonResponse<AccountType[]>>(
      environment.apiUrl + 'accounts/types',
      {
        params: new HttpParams().append('no-paginate', '1')
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

}
